import React from 'react';
import { Box, Button, Collapse, Typography, Paper } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSessionTimelineTheme } from '../theme';
import { formatEventTimestamp } from '../lib/formatTimestamp';
import { EventCardContent } from './EventCardContent';
import { TokenizedValue } from './TokenizedValue';
import { hasTokens } from '@/shared/lib/sessionTokens/parseTokens';
import type {
  ExpandState,
  RelatedEvent,
  RelatedEventData,
  ContentData,
  CustomComponentData,
} from '@/shared/ui/display/SessionTimeline';

/**
 * ContentData를 ReactNode로 변환
 */
function transformContent(content: ContentData): React.ReactNode {
  switch (content.type) {
    case 'text':
      return hasTokens(content.value)
        ? React.createElement(TokenizedValue, { value: content.value })
        : content.value;
    case 'html':
      return React.createElement('div', {
        dangerouslySetInnerHTML: { __html: content.value },
      });
    case 'component':
      console.warn(
        `Component type "${content.componentType}" is not supported. Rendering as text.`
      );
      return content.value;
    default:
      return content.value;
  }
}

/**
 * 관련 이벤트 카드 컴포넌트 (재귀적 렌더링 지원)
 */
function RelatedEventCard({
  event,
  transformedContent,
  transformedFields,
  getCardSx,
  customComponentMap,
  depth = 0,
}: {
  event: RelatedEventData;
  transformedContent?: React.ReactNode;
  transformedFields?: any[];
  getCardSx: () => any;
  customComponentMap?: Readonly<Record<string, React.ComponentType<any>>>;
  depth?: number;
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hasNestedEvents = event.relatedEvents && event.relatedEvents.length > 0;

  // 커스텀 컴포넌트 렌더링
  const renderedCustomComponent = React.useMemo<React.ReactNode | undefined>(() => {
    if (!event.customComponent || !customComponentMap) return undefined;

    const componentData = event.customComponent as CustomComponentData;
    if (customComponentMap[componentData.componentType]) {
      const Component = customComponentMap[componentData.componentType];
      return React.createElement(Component, componentData.props || {});
    }

    return null;
  }, [event.customComponent, customComponentMap]);

  return (
    <Paper
      sx={{
        ...getCardSx(),
        padding: 2.5,
      }}
    >
      <Box sx={{ marginBottom: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                marginBottom: 0.5,
                fontSize: '15px',
              }}
            >
              {event.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '12px',
              }}
            >
              {formatEventTimestamp(event.occurredAt)}
            </Typography>
          </Box>
          {hasNestedEvents && (
            <Button
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              endIcon={
                <Box
                  sx={{
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: '16px' }} />
                </Box>
              }
              sx={{
                textTransform: 'none',
                color: 'primary.main',
                minWidth: 'auto',
                padding: '4px 8px',
              }}
            >
              {event.relatedEventsCount || event.relatedEvents?.length || 0}개
            </Button>
          )}
        </Box>
      </Box>
      {/* 커스텀 컴포넌트가 있으면 우선 표시 */}
      {renderedCustomComponent && <Box sx={{ marginTop: 1 }}>{renderedCustomComponent}</Box>}
      {/* 커스텀 컴포넌트가 없으면 기본 content/fields 표시 */}
      {!renderedCustomComponent && (transformedContent || transformedFields) && (
        <EventCardContent content={transformedContent} fields={transformedFields} />
      )}
      {hasNestedEvents && (
        <Collapse
          in={isExpanded}
          timeout={300}
          easing={{
            enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
            exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Box
            sx={{
              marginTop: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {event.relatedEvents?.map((nestedEvent) => {
              const nestedContent = nestedEvent.content
                ? transformContent(nestedEvent.content)
                : undefined;
              const nestedFields = nestedEvent.fields
                ? nestedEvent.fields.map((field) => {
                    const valueString =
                      typeof field.value === 'string' ? field.value : String(field.value);
                    return {
                      label: field.label,
                      value: valueString,
                      icon: undefined,
                      copyable: field.copyable,
                    };
                  })
                : undefined;

              return (
                <RelatedEventCard
                  key={nestedEvent.id}
                  event={nestedEvent}
                  transformedContent={nestedContent}
                  transformedFields={nestedFields}
                  getCardSx={getCardSx}
                  customComponentMap={customComponentMap}
                  depth={depth + 1}
                />
              );
            })}
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}

interface EventCardRelatedEventsProps {
  relatedEvents?: readonly RelatedEvent[] | readonly RelatedEventData[];
  relatedEventsCount?: number;
  expandState: ExpandState;
  sessionId: string;
  onToggleTimeline: () => void;
  onRelatedEventsClick?: (sessionId: string) => void;
  customComponentMap?: Readonly<Record<string, React.ComponentType<any>>>;
}

export function EventCardRelatedEvents({
  relatedEvents,
  relatedEventsCount,
  expandState,
  sessionId,
  onToggleTimeline,
  onRelatedEventsClick,
  customComponentMap,
}: EventCardRelatedEventsProps) {
  const { getRelatedEventsButtonSx, getCardSx, tokens } = useSessionTimelineTheme();

  // content 또는 timeline 상태에서만 표시
  if (expandState === 'collapsed') {
    return null;
  }

  const count = relatedEventsCount ?? relatedEvents?.length ?? 0;
  const isTimelineExpanded = expandState === 'timeline';

  const handleClick = () => {
    // onRelatedEventsClick이 있으면 호출 (부모에게 알림)
    if (onRelatedEventsClick) {
      onRelatedEventsClick(sessionId);
    }

    // 항상 타임라인 토글 실행
    onToggleTimeline();
  };

  return (
    <Box sx={{ marginTop: 2 }}>
      <Button
        onClick={handleClick}
        endIcon={
          <Box
            sx={{
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: isTimelineExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: '18px' }} />
          </Box>
        }
        sx={getRelatedEventsButtonSx()}
        aria-expanded={isTimelineExpanded}
        aria-controls={`related-events-${sessionId}`}
      >
        관련 이벤트 {count}개
      </Button>
      <Collapse
        in={isTimelineExpanded}
        timeout={300}
        easing={{
          enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
          exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {relatedEvents && relatedEvents.length > 0 && (
          <Box
            id={`related-events-${sessionId}`}
            sx={{
              marginTop: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {relatedEvents.map((event, idx) => {
              // RelatedEvent 또는 RelatedEventData 처리
              const eventData: RelatedEventData = {
                id: event.id,
                title: event.title,
                occurredAt:
                  typeof event.occurredAt === 'string'
                    ? event.occurredAt
                    : event.occurredAt.toISOString(),
                content: 'content' in event ? event.content : undefined,
                fields: 'fields' in event ? event.fields : undefined,
                relatedEvents: 'relatedEvents' in event ? event.relatedEvents : undefined,
                relatedEventsCount:
                  'relatedEventsCount' in event ? event.relatedEventsCount : undefined,
                customComponent: 'customComponent' in event ? event.customComponent : undefined,
              };

              // 컨텐츠 변환
              const transformedContent = eventData.content
                ? transformContent(eventData.content)
                : undefined;

              // 필드 변환 (EventFieldData를 EventField로 변환)
              const transformedFields = eventData.fields
                ? eventData.fields.map((field) => {
                    const valueString =
                      typeof field.value === 'string' ? field.value : String(field.value);
                    return {
                      label: field.label,
                      value: valueString,
                      icon: undefined, // 관련 이벤트는 아이콘 생략
                      copyable: field.copyable,
                    };
                  })
                : undefined;

              return (
                <RelatedEventCard
                  key={eventData.id}
                  event={eventData}
                  transformedContent={transformedContent}
                  transformedFields={transformedFields}
                  getCardSx={getCardSx}
                  customComponentMap={customComponentMap}
                />
              );
            })}
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
